import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { companyApi } from '@/api';
import {
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  ListCard,
  LoadingState,
  MetricCard,
  PageHeader,
  Pagination,
  SearchInput,
  SegmentedTabs,
  Select,
  ViewButton,
} from '@/components/ui';
import { isCandidateRestricted } from '@/features/applications/accountStanding';
import { CandidateRestrictionBadge } from '@/features/applications/CandidateRestriction';
import { APPLICATION_LABELS, APPLICATION_TONES } from '@/features/applications/status';
import { PremiumBadge } from '@/features/studentBilling/PremiumBadge';
import { errorMessage } from '@/hooks/useApiError';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { PERMISSIONS } from '@/types/roles';
import { formatDate } from '@/utils/format';

const TABS = [
  { key: '', label: 'All' },
  { key: 'SUBMITTED', label: 'New' },
  { key: 'UNDER_REVIEW', label: 'In review' },
  { key: 'SHORTLISTED', label: 'Shortlisted' },
  { key: 'INTERVIEW', label: 'Interview' },
  { key: 'DECISION_PENDING', label: 'Decision pending' },
  { key: 'OFFERED', label: 'Offered' },
  { key: 'OFFER_ACCEPTED', label: 'Accepted' },
  { key: 'HIRED', label: 'Hired' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'WITHDRAWN', label: 'Withdrawn' },
];

const PAGE_SIZE = 20;

/**
 * The pipeline at a glance, from the same counts the tabs show. Each card
 * groups the statuses it names and, clicked, filters the list to exactly
 * those -- so the number on the card is the number of rows it opens.
 */
const SUMMARY = [
  { key: '', label: 'Total applications', icon: 'application', tone: 'brand', statuses: null },
  {
    key: 'group:awaiting',
    label: 'Awaiting review',
    icon: 'clockOutline',
    tone: 'warning',
    statuses: ['SUBMITTED', 'UNDER_REVIEW'],
  },
  {
    key: 'group:process',
    label: 'In the process',
    icon: 'calendarClock',
    tone: 'info',
    statuses: ['SHORTLISTED', 'INTERVIEW', 'DECISION_PENDING', 'OFFERED', 'OFFER_ACCEPTED'],
  },
  { key: 'HIRED', label: 'Hired', icon: 'userCheck', tone: 'success', statuses: ['HIRED'] },
];

/** What a filter key asks the API for: a summary card's group, or one status. */
function statusesFor(key) {
  if (!key) return undefined;
  return SUMMARY.find((card) => card.key === key)?.statuses ?? [key];
}

export function CompanyApplicationsPage() {
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  // Order and listing live in the address, so a link (the sidebar's ranked
  // view, "Applicants by fit" on AI recommendations) opens them preset.
  const [searchParams, setSearchParams] = useSearchParams();
  const sort = searchParams.get('sort') === 'match' ? 'match' : 'recent';
  const internshipId = searchParams.get('internship') ?? '';
  const setParam = (key, value) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  const setSort = (value) => setParam('sort', value === 'match' ? 'match' : '');
  const setInternshipId = (value) => setParam('internship', value);
  const [page, setPage] = useState(1);

  const params = {
    page,
    page_size: PAGE_SIZE,
    ...(status ? { statuses: statusesFor(status) } : {}),
    ...(internshipId ? { internship_id: internshipId } : {}),
    ...(search.trim() ? { q: search.trim() } : {}),
    sort,
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['company-applications', params],
    queryFn: () => companyApi.listApplications(params),
    // Keeps the last page on screen while the next one loads, so typing in
    // the search box does not swap the table for a spinner on every key.
    placeholderData: keepPreviousData,
  });

  // The same internship and search as the list, so a tab's count is the
  // rows that tab shows.
  const countParams = {
    ...(internshipId ? { internship_id: internshipId } : {}),
    ...(search.trim() ? { q: search.trim() } : {}),
  };
  const { data: counts } = useQuery({
    queryKey: ['company-application-counts', countParams],
    queryFn: () => companyApi.getApplicationCounts(countParams),
    placeholderData: keepPreviousData,
  });

  // Only published and closed listings can have applicants worth filtering by.
  const { can } = usePermissions();
  // The filter lists internships, which is its own permission.
  const canReadInternships = can(PERMISSIONS.INTERNSHIP_READ);
  const { data: internships } = useQuery({
    queryKey: ['company-internships', 'filter'],
    queryFn: () => companyApi.listInternships({ page: 1, page_size: 100 }),
    enabled: canReadInternships,
  });

  const activeCount = [status !== '', internshipId !== '', search !== ''].filter(Boolean).length;

  function resetFilters() {
    setStatus('');
    setInternshipId('');
    setSearch('');
    setPage(1);
  }

  const tabs = TABS.map((tab) => ({
    ...tab,
    count: counts && tab.key ? (counts[tab.key] ?? 0) : undefined,
  }));

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div>
      <PageHeader
        title="Applications"
        description="Everyone who applied to your internships, and where they are."
      />

      {counts && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {SUMMARY.map((card) => {
            const value = card.statuses
              ? card.statuses.reduce((sum, key) => sum + (counts[key] ?? 0), 0)
              : Object.values(counts).reduce((sum, count) => sum + (count ?? 0), 0);
            return (
              <MetricCard
                key={card.label}
                label={card.label}
                value={value}
                icon={card.icon}
                tone={card.tone}
                active={status === card.key}
                onClick={() => {
                  setStatus(card.key);
                  setPage(1);
                }}
              />
            );
          })}
        </div>
      )}

      <ListCard
        isRefreshing={isPlaceholderData}
        toolbarStart={
          <SegmentedTabs
            variant="plain"
            label="Application status"
            tabs={tabs}
            value={status}
            onChange={(key) => {
              setStatus(key);
              setPage(1);
            }}
          />
        }
        toolbarEnd={
          <>
            {canReadInternships && (
              <Select
                label="Internship"
                hideLabel
                size="sm"
                wrapperClassName="w-full sm:w-44"
                value={internshipId}
                onChange={(event) => {
                  setInternshipId(event.target.value);
                  setPage(1);
                }}
              >
                <option value="">All internships</option>
                {(internships?.data ?? []).map((internship) => (
                  <option key={internship.id} value={internship.id}>
                    {internship.title}
                  </option>
                ))}
              </Select>
            )}
            <Select
              label="Order"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-36"
              value={sort}
              onChange={(event) => {
                setSort(event.target.value);
                setPage(1);
              }}
            >
              <option value="recent">Most recent</option>
              <option value="match">Best fit</option>
            </Select>
            <SearchInput
              label="Search by name"
              hideLabel
              size="sm"
              className="w-full sm:w-64"
              placeholder="Search by name"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              onClear={() => {
                setSearch('');
                setPage(1);
              }}
            />
            {activeCount > 0 && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <Icon name="reset" size={14} />
                Reset
              </Button>
            )}
          </>
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading applications…" />
          ) : rows.length === 0 ? (
            activeCount > 0 ? (
              <EmptyState
                icon={<Icon name="application" size={22} />}
                title="No applications here"
                description="Nothing matches these filters."
                action={
                  <Button variant="secondary" size="sm" onClick={resetFilters}>
                    Reset filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={<Icon name="application" size={22} />}
                title="No applications here"
                description="Publish an internship and applications will show up here."
                action={
                  canReadInternships && (
                    <LinkButton to={paths.company.internships} variant="secondary" size="sm">
                      Your internships
                    </LinkButton>
                  )
                }
              />
            )
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((row) => (
            <BoxRow
              key={row.id}
              // A suspended candidate's application is kept but frozen: the
              // row fades back and carries a danger stripe, and still opens.
              muted={isCandidateRestricted(row)}
              accent={
                isCandidateRestricted(row) ? 'danger' : (APPLICATION_TONES[row.status] ?? 'neutral')
              }
              leading={<BoxAvatar name={row.student.full_name} round />}
              title={row.student.full_name}
              to={paths.company.applicationDetail(row.id)}
              badges={
                <>
                  <Badge dot tone={APPLICATION_TONES[row.status] ?? 'neutral'}>
                    {APPLICATION_LABELS[row.status] ?? row.status.replace('_', ' ')}
                  </Badge>
                  <CandidateRestrictionBadge row={row} />
                  {/* Applications made before matching existed have no
                      score, and none is invented for them. */}
                  {row.match_score != null && (
                    <Badge tone="brand">{Math.round(row.match_score)}% match</Badge>
                  )}
                  {row.is_premium && <PremiumBadge />}
                </>
              }
              // Which of the company's listings this is, said in a sentence.
              subtitle={
                <span className="text-ink-muted">
                  Applied for{' '}
                  {/* Without read access the title stays plain text rather
                      than leading to "access denied". */}
                  {canReadInternships ? (
                    <Link
                      to={paths.company.internshipDetail(row.internship_id)}
                      onClick={(event) => event.stopPropagation()}
                      className="text-ink hover:text-brand-700 relative font-semibold underline-offset-2 hover:underline"
                    >
                      {row.internship_title}
                    </Link>
                  ) : (
                    <span className="text-ink font-semibold">{row.internship_title}</span>
                  )}
                </span>
              }
              description={row.student.headline}
              facts={[
                row.student.email && { icon: 'user', text: row.student.email },
                { icon: 'calendar', text: `Applied ${formatDate(row.created_at)}` },
                row.has_resume
                  ? { icon: 'fileText', text: 'CV attached', tone: 'success' }
                  : { icon: 'fileText', text: 'No CV' },
              ]}
              meta={
                row.reviewed_at && (
                  <span className="text-ink-muted text-xs">
                    Reviewed {formatDate(row.reviewed_at)}
                  </span>
                )
              }
              actions={
                <ViewButton
                  to={paths.company.applicationDetail(row.id)}
                  title={`View ${row.student.full_name}'s application`}
                  label="View application"
                  icon="fileText"
                />
              }
            />
          ))}
        </BoxList>
      </ListCard>
    </div>
  );
}
